"use client";

import { useState } from "react";
import { SenseiMarkLoader } from "@/shared/components/layout/SenseiMarkLoader";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  email: string;
  company: string;
  role: string;
  message: string;
}

type SubmitStatus = "idle" | "loading" | "success" | "error";

// ─── Validation ──────────────────────────────────────────────────────────────

type FormErrors = Partial<Record<keyof FormState, string>>;

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim())
    errors.name = "Full name is required.";
  if (!form.email.trim())
    errors.email = "Work email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    errors.email = "Enter a valid email address.";
  if (!form.message.trim())
    errors.message = "Please tell us what you're trying to solve.";
  return errors;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbw-kJFSBV9M1MI6tBb9bAkKd35ajXWUUw2U0KcIG_jShcJLiyibNY8DAqsRGItPpFA/exec";

const ROLES = [
  "Supply Chain Manager",
  "Procurement Lead",
  "Operations Director",
  "Logistics Manager",
  "C-Suite / VP",
  "Consultant / Advisor",
  "Other",
];

const INITIAL_STATE: FormState = {
  name: "",
  email: "",
  company: "",
  role: "",
  message: "",
};

// ─── Shared input styles ──────────────────────────────────────────────────────

const inputBase: React.CSSProperties = {
  width: "100%",
  background: "var(--paper-3)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--line-strong)",
  borderRadius: 10,
  padding: "11px 14px",
  fontSize: 15,
  fontFamily: "inherit",
  color: "var(--ink)",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--ink-soft)",
  marginBottom: 6,
};

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const errorTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#f87171",
  marginTop: 4,
};

// ─── Component ───────────────────────────────────────────────────────────────

export function ContactForm() {
  const [form, setForm]           = useState<FormState>(INITIAL_STATE);
  const [status, setStatus]       = useState<SubmitStatus>("idle");
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors]       = useState<FormErrors>({});
  const [touched, setTouched]     = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field error as soon as the user edits it
    if (errors[name as keyof FormState]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setFocusedField(null);
    setTouched((prev) => ({ ...prev, [name]: true }));
    // Validate single field on blur
    const fieldErrors = validate({ ...form, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name as keyof FormState] }));
  }

  function getInputStyle(name: string, hasError?: boolean): React.CSSProperties {
    return {
      ...inputBase,
      ...(focusedField === name && !hasError
        ? { borderColor: "var(--indigo)", boxShadow: "0 0 0 3px rgba(91,108,255,0.18)" }
        : {}),
      ...(hasError
        ? { borderColor: "#f87171", boxShadow: focusedField === name ? "0 0 0 3px rgba(248,113,113,0.18)" : "none" }
        : {}),
    };
  }

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    // Mark all required fields as touched and run full validation
    const allTouched = { name: true, email: true, company: true, role: true, message: true };
    setTouched(allTouched);
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    if (!GOOGLE_SCRIPT_URL) {
      setStatus("error");
      setSubmitError("Contact endpoint is not configured. Please set NEXT_PUBLIC_CONTACT_FORM_ENDPOINT.");
      return;
    }

    setStatus("loading");
    setSubmitError("");

    const params = new URLSearchParams({
      name: form.name,
      email: form.email,
      company: form.company,
      role: form.role,
      message: form.message,
      submittedAt: new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }),
      source: "sensei-contact-page",
    });

    const res = await fetch(GOOGLE_SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      body: params,
    });

    if (res.type === "opaque" || res.ok) {
      setStatus("success");
      setForm(INITIAL_STATE);
      setTouched({});
      setErrors({});
    } else {
      setStatus("error");
      setSubmitError("Something went wrong. Please try again.");
    }
  }

  // ── Success state ──────────────────────────────────────────────────────────

  if (status === "success") {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          textAlign: "center", gap: 16, padding: "24px 0 8px",
          animation: "fadeUp .4s ease both",
        }}
      >
        <div style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 64, height: 64, borderRadius: "50%",
          background: "rgba(34,197,94,0.12)", color: "var(--green)",
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h3 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--ink)", margin: 0 }}>
          Message received
        </h3>
        <p style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.6, maxWidth: "36ch", margin: 0 }}>
          Thanks for reaching out. Someone from our team will get back to you within one business day.
        </p>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => { setStatus("idle"); setSubmitError(""); }}
        >
          Send another message
        </button>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────

  const isLoading = status === "loading";

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-label="Get in touch"
      style={{ display: "flex", flexDirection: "column", gap: 20 }}
    >
      {/* Row: name + email */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={fieldStyle}>
          <label htmlFor="cf-name" style={labelStyle}>
            Full name <span style={{ color: "var(--magenta)" }} aria-hidden="true">*</span>
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            placeholder="Jane Smith"
            value={form.name}
            onChange={handleChange}
            onFocus={() => setFocusedField("name")}
            onBlur={handleBlur}
            style={getInputStyle("name", !!(touched.name && errors.name))}
            autoComplete="name"
          />
          {touched.name && errors.name && (
            <span style={errorTextStyle} role="alert">{errors.name}</span>
          )}
        </div>
        <div style={fieldStyle}>
          <label htmlFor="cf-email" style={labelStyle}>
            Work email <span style={{ color: "var(--magenta)" }} aria-hidden="true">*</span>
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            placeholder="jane@company.com"
            value={form.email}
            onChange={handleChange}
            onFocus={() => setFocusedField("email")}
            onBlur={handleBlur}
            style={getInputStyle("email", !!(touched.email && errors.email))}
            autoComplete="email"
          />
          {touched.email && errors.email && (
            <span style={errorTextStyle} role="alert">{errors.email}</span>
          )}
        </div>
      </div>

      {/* Row: company + role */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={fieldStyle}>
          <label htmlFor="cf-company" style={labelStyle}>Company</label>
          <input
            id="cf-company"
            name="company"
            type="text"
            placeholder="Acme Corp"
            value={form.company}
            onChange={handleChange}
            onFocus={() => setFocusedField("company")}
            onBlur={handleBlur}
            style={getInputStyle("company")}
            autoComplete="organization"
          />
        </div>
        <div style={fieldStyle}>
          <label htmlFor="cf-role" style={labelStyle}>Your role</label>
          <select
            id="cf-role"
            name="role"
            value={form.role}
            onChange={handleChange}
            onFocus={() => setFocusedField("role")}
            onBlur={handleBlur}
            style={{
              ...getInputStyle("role"),
              appearance: "none",
              WebkitAppearance: "none",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.45)' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 14px center",
              paddingRight: 36,
              cursor: "pointer",
            }}
          >
            <option value="">Select a role…</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Message */}
      <div style={fieldStyle}>
        <label htmlFor="cf-message" style={labelStyle}>
          Message <span style={{ color: "var(--magenta)" }} aria-hidden="true">*</span>
        </label>
        <textarea
          id="cf-message"
          name="message"
          placeholder="Tell us what you're trying to solve…"
          rows={5}
          value={form.message}
          onChange={handleChange}
          onFocus={() => setFocusedField("message")}
          onBlur={handleBlur}
          style={{
            ...getInputStyle("message", !!(touched.message && errors.message)),
            resize: "vertical",
            minHeight: 120,
            lineHeight: 1.55,
          }}
        />
        {touched.message && errors.message && (
          <span style={errorTextStyle} role="alert">{errors.message}</span>
        )}
      </div>

      {/* Submit error banner */}
      {status === "error" && submitError && (
        <p
          role="alert"
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "12px 14px", borderRadius: 10,
            background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)",
            color: "#f87171", fontSize: 14, lineHeight: 1.45, margin: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {submitError}
        </p>
      )}

      {/* Submit button — outlined + spinner only while loading */}
      <button
        type="submit"
        disabled={isLoading}
        aria-busy={isLoading}
        className={isLoading ? "btn btn-ghost" : "btn btn-primary"}
        style={{ justifyContent: "center" }}
      >
        {isLoading ? (
          <SenseiMarkLoader size={22} />
        ) : (
          <>
            Send message
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </>
        )}
      </button>
    </form>
  );
}
